import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationWithHierarchyType } from '../../contexts/types';
import {
  NODE_BASE_HEIGHT,
  NODE_WIDTH,
  ORG_NAME_ABBREVIATE_LENGTH,
  PAD,
  PositionedNode,
  buildAndLayoutTrees,
  getBounds,
  getScopedVisibleOrgIds,
  groupByParent,
} from '../../utils/organizationTree';

interface OrgChartSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgList: OrganizationWithHierarchyType[];
  addedIds: string[];       // 追加済み組織ID（緑ハイライト表示）
  onToggle?: (orgId: string) => void;  // クリックで追加/削除切り替え（即時反映モード）
  disabledIds?: string[];  // 選択済みかつ変更不可のID
  /** 保存ボタンモード: 提供時はローカル選択状態を管理し、保存ボタン押下時に確定 */
  onSave?: (selectedIds: string[]) => void;
  /** 表示専用モード: true時はノードクリック無効・カーソルもdefaultにする（選択/追加削除機能なし） */
  readOnly?: boolean;
  /** モーダルヘッダーの翻訳キー（未指定時は組織選択モーダル用の既定タイトルを使用） */
  titleKey?: string;
  /** 表示範囲を「対象ノード＋祖先チェーン＋兄弟ノード」に絞り込むか（readOnly時のみ有効） */
  scopeToAddedIds?: boolean;
}

/** 拡大縮小の刻み幅・下限・上限（ボタン操作・ホイール操作で共通利用） */
const SCALE_STEP = 0.1;
const SCALE_MIN = 0.5;
const SCALE_MAX = 2.0;
/** この移動量（px）未満はクリック、以上はドラッグ（パン）と判定する閾値 */
const DRAG_THRESHOLD = 5;

const OrgChartSelectModal: React.FC<OrgChartSelectModalProps> = ({
  isOpen,
  onClose,
  orgList,
  addedIds,
  onToggle,
  disabledIds = [],
  onSave,
  readOnly = false,
  titleKey,
  scopeToAddedIds = false,
}) => {
  const { t } = useTranslation();
  const [scale, setScale] = useState(0.8);
  const [searchWord, setSearchWord] = useState('');
  // onSave モード用ローカル選択状態
  const [localAddedIds, setLocalAddedIds] = useState<string[]>([]);

  // ズーム・パン用
  // const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollContainerRef, setScrollContainerRef] = useState<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
    moved: false,
  });
  // ドラッグ確定直後のノードクリックを1回だけ無視するためのフラグ
  const suppressNextClickRef = useRef(false);

  /**
   * scale を範囲内（SCALE_MIN〜SCALE_MAX）でクランプしつつ更新する
   * @param updater 現在値から次の値を計算する関数
   */
  const updateScale = (updater: (prev: number) => number) => {
    setScale(prev => {
      const next = updater(prev);
      return Math.min(SCALE_MAX, Math.max(SCALE_MIN, +next.toFixed(1)));
    });
  };

  /**
   * カーソル位置を中心にホイールでズームする
   * ブラウザのネイティブスクロールとのタイミングずれを避けるため、
   * preventDefaultと同じタイミングでscale・スクロール位置を同期的に更新する
   * @param e ホイールイベント
   */
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const container = scrollContainerRef;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const deltaY = e.deltaY;

    setScale(oldScale => {
      const delta = deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
      const newScale = Math.min(SCALE_MAX, Math.max(SCALE_MIN, +(oldScale + delta).toFixed(1)));
      if (newScale === oldScale) return oldScale;

      const contentX = (container.scrollLeft + cursorX) / oldScale;
      const contentY = (container.scrollTop + cursorY) / oldScale;
      container.scrollLeft = contentX * newScale - cursorX;
      container.scrollTop = contentY * newScale - cursorY;
      return newScale;
    });
  };

  /**
   * ドラッグパン開始（マウスダウン時に開始座標とスクロール位置を記録）
   * @param e マウスイベント
   */
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // const container = scrollContainerRef.current;
    const container = scrollContainerRef;
    if (!container) return;
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startScrollLeft: container.scrollLeft,
      startScrollTop: container.scrollTop,
      moved: false,
    };
    setIsDragging(true);
  };

  /**
   * ドラッグパン中の移動処理（移動量が閾値を超えたら「実際に動いた」扱いにする）
   * @param e マウスイベント
   */
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    // const container = scrollContainerRef.current;
    const container = scrollContainerRef;
    if (!container) return;

    const dragState = dragStateRef.current;
    const diffX = e.clientX - dragState.startX;
    const diffY = e.clientY - dragState.startY;
    if (!dragState.moved && (Math.abs(diffX) >= DRAG_THRESHOLD || Math.abs(diffY) >= DRAG_THRESHOLD)) {
      dragState.moved = true;
    }
    container.scrollLeft = dragState.startScrollLeft - diffX;
    container.scrollTop = dragState.startScrollTop - diffY;
  };

  /** ドラッグパン終了。実際に動いていた場合は直後のノードクリックを1回だけ無視する */
  const endDrag = () => {
    if (!isDragging) return;
    if (dragStateRef.current.moved) {
      suppressNextClickRef.current = true;
    }
    setIsDragging(false);
  };

  useEffect(() => {
    // DOM 要素が実際にマウントされるまで待機（マウント時に自動再実行される）
    if (!scrollContainerRef) return;

    const handleWheel = (e: WheelEvent) => {
      // マウスホイールによるスクロールを抑止
      e.preventDefault();
    };

    // ReactのJSX属性（onWheel）経由では自動的に passive: true として登録されてしまうので、DOM要素にて対応
    scrollContainerRef.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      // モーダル非表示時にイベントリスナー登録解除
      scrollContainerRef.removeEventListener('wheel', handleWheel);
    };
  }, [scrollContainerRef]);

  useEffect(() => {
    if (!isOpen) {
      setSearchWord('');
    } else if (onSave) {
      setLocalAddedIds([...addedIds]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const getDescendantIds = (orgId: string): string[] => {
    const children = orgList.filter(o => o.parentOrganizationId === orgId);
    return children.flatMap(c => [c.organizationId, ...getDescendantIds(c.organizationId)]);
  };

  const getAncestorIds = (orgId: string): string[] => {
    const ancestors: string[] = [];
    let currentId = orgId;
    for (; ;) {
      const org = orgList.find(o => o.organizationId === currentId);
      if (!org || !org.parentOrganizationId) break;
      ancestors.push(org.parentOrganizationId);
      currentId = org.parentOrganizationId;
    }
    return ancestors;
  };

  const localDisabledIds = useMemo(() => {
    if (!onSave) return disabledIds;
    return orgList
      .filter(org => {
        if (localAddedIds.includes(org.organizationId)) return false;
        return getAncestorIds(org.organizationId).some(aid => localAddedIds.includes(aid));
      })
      .map(o => o.organizationId);
  }, [onSave, localAddedIds, orgList, disabledIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveAddedIds = onSave ? localAddedIds : addedIds;
  const effectiveDisabledIds = onSave ? localDisabledIds : disabledIds;

  const handleNodeClick = (orgId: string) => {
    if (readOnly) return;
    if (onSave) {
      const isAdded = localAddedIds.includes(orgId);
      if (isAdded) {
        // ① すでに選択されている場合は、選択を解除
        setLocalAddedIds(prev => prev.filter(id => id !== orgId));
      } else {
        // ② 未選択の場合は選択する。ただし、その子孫ノードが選択されていたら解除する
        const descendantIds = new Set(getDescendantIds(orgId));
        setLocalAddedIds(prev => [
          ...prev.filter(id => !descendantIds.has(id)),
          orgId,
        ]);
      }
    } else {
      // onSave がない場合は、外部から渡されたトグル関数をそのまま実行
      onToggle?.(orgId);
    }
  };

  const scopedOrgList = useMemo(() => {
    if (!readOnly || !scopeToAddedIds) return orgList;
    const visibleIds = getScopedVisibleOrgIds(orgList, addedIds);
    return orgList.filter(o => visibleIds.has(o.organizationId));
  }, [orgList, readOnly, scopeToAddedIds, addedIds]);

  const positionedTrees = useMemo(() => {
    const orgsByParent = groupByParent(scopedOrgList);
    const orgIds = new Set(scopedOrgList.map(o => o.organizationId));
    const roots = scopedOrgList.filter(o => !o.parentOrganizationId || !orgIds.has(o.parentOrganizationId));
    return buildAndLayoutTrees(roots, orgsByParent);
  }, [scopedOrgList]);

  const bounds = useMemo(() => getBounds(positionedTrees), [positionedTrees]);

  const getNodeStyle = (orgId: string) => {
    const isDisabled = effectiveDisabledIds.includes(orgId);
    const isAdded = effectiveAddedIds.includes(orgId);
    return {
      fill: isAdded ? '#C6F6D5' : '#ffffff',
      stroke: isAdded ? '#38A169' : '#9ca3af',
      strokeWidth: isAdded ? 2.5 : 1.5,
      opacity: isDisabled ? 0.5 : 1,
    };
  };

  const getOpacity = (orgName: string): number => {
    if (!searchWord) return 1;
    return orgName.includes(searchWord) ? 1 : 0.2;
  };

  const renderLines = (nodes: PositionedNode[]): JSX.Element[] => {
    return nodes.flatMap(node =>
      node.children.flatMap(child => [
        <line
          key={`line-${node.org.organizationId}-${child.org.organizationId}`}
          x1={node.x + NODE_WIDTH}
          y1={node.y + NODE_BASE_HEIGHT / 2}
          x2={child.x}
          y2={child.y + NODE_BASE_HEIGHT / 2}
          stroke="#9ca3af"
          strokeWidth={1.5}
        />,
        ...renderLines([child]),
      ])
    );
  };

  const renderNodes = (nodes: PositionedNode[]): JSX.Element[] => {
    return nodes.flatMap(node => {
      const { fill, stroke, strokeWidth, opacity: nodeOpacity } = getNodeStyle(node.org.organizationId);
      const searchOpacity = getOpacity(node.org.organizationName);
      const label = node.org.organizationName.length > ORG_NAME_ABBREVIATE_LENGTH
        ? node.org.organizationName.slice(0, ORG_NAME_ABBREVIATE_LENGTH) + '…'
        : node.org.organizationName;
      return [
        <g
          key={node.org.organizationId}
          opacity={nodeOpacity * searchOpacity}
          style={{ cursor: readOnly ? 'default' : effectiveDisabledIds.includes(node.org.organizationId) ? 'not-allowed' : 'pointer' }}
          onClick={() => {
            if (suppressNextClickRef.current) {
              // 直前のドラッグ操作によるクリック発火を無視する
              suppressNextClickRef.current = false;
              return;
            }
            if (readOnly) return;
            if (effectiveDisabledIds.includes(node.org.organizationId)) return;
            handleNodeClick(node.org.organizationId);
          }}
        >
          <rect
            x={node.x} y={node.y}
            width={NODE_WIDTH} height={NODE_BASE_HEIGHT}
            rx={8} ry={8}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth}
          />
          <text
            x={node.x + 14}
            y={node.y + NODE_BASE_HEIGHT / 2}
            dominantBaseline="middle"
            fontSize={14}
            fontWeight={700}
            fill="#111827"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {label}
          </text>
        </g>,
        ...renderNodes(node.children),
      ];
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent minW="60vw">
        <ModalHeader>
          {t(titleKey ?? 'features.MasterMaintenance.OrgViewAccessPanel.modal.orgChart.title')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            mb={3}
            size="sm"
            value={searchWord}
            onChange={e => setSearchWord(e.target.value)}
            placeholder={t('features.MasterMaintenance.OrgViewAccessPanel.modal.orgChart.searchPlaceholder')}
          />
          <HStack mb={2} spacing={1}>
            <IconButton
              aria-label="zoom in"
              icon={<AddIcon />}
              size="xs"
              onClick={() => updateScale(s => s + SCALE_STEP)}
            />
            <IconButton
              aria-label="zoom out"
              icon={<MinusIcon />}
              size="xs"
              onClick={() => updateScale(s => s - SCALE_STEP)}
            />
            <Text fontSize="xs" color="gray.500">{Math.round(scale * 100)}%</Text>
          </HStack>
          <Box
            ref={setScrollContainerRef}
            overflow="auto"
            maxH="62vh"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
            cursor={isDragging ? 'grabbing' : 'grab'}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onClick={() => {
              // ノード以外の余白でのクリックではノード側onClickが発火しないため、ここでフラグを確実にリセットする
              suppressNextClickRef.current = false;
            }}
          >
            <svg
              width={(bounds.width + PAD * 2) * scale}
              height={(bounds.height + PAD * 2) * scale}
            >
              <g transform={`scale(${scale}) translate(${PAD - bounds.minX}, ${PAD - bounds.minY})`}>
                {renderLines(positionedTrees)}
                {renderNodes(positionedTrees)}
              </g>
            </svg>
          </Box>
        </ModalBody>
        <ModalFooter>
          {onSave && (
            <Button colorScheme="blue" onClick={() => onSave(localAddedIds)} mr={2}>
              {t('common.button.save')}
            </Button>
          )}
          <Button colorScheme={onSave ? "gray" : "blue"} onClick={onClose}>
            {t('common.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

OrgChartSelectModal.displayName = 'OrgChartSelectModal';
export default OrgChartSelectModal;
